/**
 * ShieldWall — Payload Encryption (E2EE)
 *
 * Encrypts the verify payload with RSA-AES hybrid encryption so the
 * Network tab shows only opaque ciphertext. The server issues a fresh
 * RSA-2048 public key per challenge; the client wraps a random AES-256-GCM
 * session key with it, encrypts the JSON payload, and sends the envelope.
 *
 * Security design:
 *   - AES-256-GCM provides authenticated encryption (integrity + confidentiality)
 *   - RSA-OAEP-SHA256 wraps the AES key (only the server's private key can unwrap)
 *   - Per-challenge keys: compromising one key reveals only that single payload
 *
 * @module payload-crypto
 */

/** Encrypted envelope sent to the server in place of plain JSON. */
export interface EncryptedEnvelope {
  /** RSA-OAEP wrapped AES-256-GCM key, base64. */
  encryptedKey: string;
  /** AES-GCM initialization vector, base64. */
  iv: string;
  /** AES-GCM ciphertext (contains the JSON payload), base64. */
  ciphertext: string;
}

const encoder = new TextEncoder();

/**
 * Import an RSA public key from SPKI PEM format for RSA-OAEP wrapping.
 */
async function importPublicKey(pem: string): Promise<CryptoKey> {
  // Strip PEM header/footer and whitespace
  const pemBody = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, "")
    .replace(/-----END PUBLIC KEY-----/, "")
    .replace(/\s+/g, "");

  const der = Uint8Array.from(atob(pemBody), (c) => c.charCodeAt(0));

  return crypto.subtle.importKey(
    "spki",
    der,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["wrapKey"]
  );
}

/**
 * Encrypt a verify payload using RSA-AES hybrid encryption.
 *
 * 1. Generate a random AES-256-GCM key
 * 2. Encrypt JSON.stringify(payload) with AES-GCM → { ciphertext, iv }
 * 3. Wrap the AES key with the server's RSA public key (RSA-OAEP-SHA256)
 * 4. Return the envelope (all fields base64-encoded)
 *
 * @param publicKeyPem - Server-provided RSA-2048 SPKI PEM public key
 * @param payload - The plain verify body object to encrypt
 */
export async function encryptPayload(
  publicKeyPem: string,
  payload: object
): Promise<EncryptedEnvelope> {
  // 1. Generate ephemeral AES-256-GCM key
  const aesKey = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true, // extractable — so we can wrap it with RSA
    ["encrypt"]
  );

  // 2. Encrypt the JSON payload with AES-GCM
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV (NIST standard)
  const plaintext = encoder.encode(JSON.stringify(payload));
  const ciphertextBuf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, tagLength: 128 },
    aesKey,
    plaintext
  );

  // 3. Wrap AES key with server's RSA public key (RSA-OAEP-SHA256)
  const rsaPublicKey = await importPublicKey(publicKeyPem);
  const wrappedKeyBuf = await crypto.subtle.wrapKey(
    "raw",
    aesKey,
    rsaPublicKey,
    { name: "RSA-OAEP" }
  );

  // 4. Encode everything as base64
  return {
    encryptedKey: bufToBase64(wrappedKeyBuf),
    iv: bufToBase64(iv),
    ciphertext: bufToBase64(ciphertextBuf),
  };
}

/**
 * Check if payload encryption is supported in this environment.
 * Requires Web Crypto API with RSA-OAEP and AES-GCM support.
 */
export function isEncryptionSupported(): boolean {
  return (
    typeof crypto !== "undefined" &&
    typeof crypto.subtle !== "undefined" &&
    typeof crypto.subtle.generateKey === "function" &&
    typeof crypto.subtle.wrapKey === "function"
  );
}

function bufToBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}
